"use client";

import { NodeViewWrapper } from "@tiptap/react";
import { Edit, Plus, ShoppingBag, X } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import useSWR from "swr";
import { getProductsAction } from "@/actions/admin";
import { formatVariantsPriceRange } from "@/lib/financial/money";
import { cn, getProductImages } from "@/lib/utils";
import { Button } from "@/ui/shadcn/button";
import { Card } from "@/ui/shadcn/card";
import { ProductMultiSelect } from "./product-multi-select";
export const ProductEmbedEditor = ({
  node,
  updateAttributes
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const productIds = Array.isArray(node.attrs.productIds) ? node.attrs.productIds : [];
  console.log("ProductEmbedEditor node.attrs:", node.attrs);
  console.log("ProductEmbedEditor productIds:", productIds);

  // Fetch all products using the same pattern as ProductMultiSelect
  const {
    data: productsResult,
    isLoading
  } = useSWR("getProductsAction", () => getProductsAction(), {
    keepPreviousData: true
  });
  const allProducts = productsResult?.data || [];
  console.log("ProductEmbedEditor productsResult:", productsResult);
  console.log("ProductEmbedEditor allProducts:", allProducts);
  const _handleAddProducts = selectedProductIds => {
    updateAttributes({
      productIds: selectedProductIds
    });
    setShowPicker(false);
  };
  const handleRemoveProduct = productId => {
    const newProductIds = productIds.filter(id => id !== productId);
    updateAttributes({
      productIds: newProductIds
    });
  };
  const renderEmptyState = () => <Card className="p-6 border-dashed border-2 border-gray-300">
			<div className="text-center">
				<ShoppingBag className="mx-auto h-12 w-12 text-gray-400 mb-4" />
				<h3 className="text-lg font-medium text-gray-900 mb-2">No products selected</h3>
				<p className="text-gray-500 mb-4">Add products to showcase them in your blog post.</p>
				<Button onClick={() => setShowPicker(true)} variant="outline" type="button">
					<Plus className="w-4 h-4 mr-2" />
					Add Products
				</Button>
			</div>
		</Card>;

  // Get the selected products from all products
  const selectedProducts = productIds.map(id_0 => allProducts?.find?.(p => p?.id === id_0)).filter(Boolean);
  const renderProductCard = product => {
    if (!product) return null;
    const images = getProductImages({
      images: product.images || []
    });
    const imageUrl = images?.[0];
    const priceRange = formatVariantsPriceRange({
      taxRate: product.productTaxRate?.taxRate?.rate || 0n,
      taxBehavior: product.taxBehavior,
      variants: product.variants || [],
      currency: product.currency || "USD",
      locale: product.locale || "en-US"
    });
    return <Card key={product.id} className="relative group">
				<div className="relative aspect-square bg-gray-100 rounded-t-lg flex items-center justify-center">
					{imageUrl ? <Image src={imageUrl} alt={product.name} fill className="object-cover rounded-t-lg" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" /> : <div className="text-gray-400">
							<svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
							</svg>
						</div>}
					<Button variant="ghost" size="sm" className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white" onClick={() => handleRemoveProduct(product.id)} type="button">
						<X className="w-4 h-4" />
					</Button>
				</div>
				<div className="p-3">
					<h4 className="font-semibold text-sm truncate mb-1">{product.name}</h4>
					<p className="text-sm text-gray-600">{priceRange}</p>
				</div>
			</Card>;
  };
  if (isLoading) {
    return <NodeViewWrapper>
				<Card className="p-6">
					<div className="flex items-center justify-center">
						<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
						<span className="ml-2 text-gray-600">Loading products...</span>
					</div>
				</Card>
			</NodeViewWrapper>;
  }
  return <NodeViewWrapper>
			<div className="my-4">
				{!selectedProducts || selectedProducts.length === 0 ? renderEmptyState() : <Card className="p-4">
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center">
								<ShoppingBag className="w-5 h-5 text-gray-600 mr-2" />
								<span className="font-medium text-gray-900">
									Featured Products ({selectedProducts?.length || 0}/3)
								</span>
							</div>
							<div className="flex gap-2">
								{(selectedProducts?.length || 0) < 3 && <Button onClick={() => setShowPicker(true)} variant="outline" size="sm" type="button">
										<Plus className="w-4 h-4 mr-1" />
										Add
									</Button>}
								<Button onClick={() => setShowPicker(true)} variant="outline" size="sm" type="button">
									<Edit className="w-4 h-4 mr-1" />
									Edit
								</Button>
							</div>
						</div>

						<div className={cn("grid gap-3", selectedProducts?.length === 1 && "grid-cols-1 max-w-xs", selectedProducts?.length === 2 && "grid-cols-2", selectedProducts?.length === 3 && "grid-cols-3")}>
							{selectedProducts?.map(renderProductCard)}
						</div>
					</Card>}

				{showPicker && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
						<Card className="w-96 p-6">
							<div className="space-y-4">
								<div className="space-y-2">
									<h4 className="font-medium leading-none">Edit Featured Products</h4>
									<p className="text-sm text-muted-foreground">
										Select up to 3 products to feature in your blog post.
									</p>
								</div>
								<ProductMultiSelect placeholder="Search products..." value={productIds} onValueChange={newProductIds_0 => {
              // Limit to 3 products
              const limitedIds = newProductIds_0.slice(0, 3);
              updateAttributes({
                productIds: limitedIds
              });
            }} />
								<div className="flex justify-end gap-2">
									<Button variant="outline" size="sm" onClick={() => setShowPicker(false)} type="button">
										Done
									</Button>
								</div>
							</div>
						</Card>
					</div>}
			</div>
		</NodeViewWrapper>;
};